import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Award, BarChart3, Coins, Loader2, MousePointerClick, Repeat2 } from "lucide-react";
import { getRecap } from "@/lib/dashboard.functions";
import { readSavedItems, type SavedItem } from "@/lib/owner-store";
import { formatIDR, levelFor } from "@/lib/board-constants";
import { SiteFooter, SiteHeader } from "@/components/site-chrome";
import { CountUp, Reveal, ScrollProgress } from "@/components/motion-kit";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export const Route = createFileRoute("/rekap")({
  head: () => ({
    meta: [
      { title: "Rekap Performa Listing — jasasebar.com" },
      {
        name: "description",
        content: "Rekap poin, badge, klik, dan hasil auto-bid listing Anda per periode di jasasebar.com.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: RecapPage,
});

const PERIODS = [
  { days: 7, label: "7 hari" },
  { days: 30, label: "30 hari" },
  { days: 90, label: "90 hari" },
];

function RecapPage() {
  const [items, setItems] = useState<SavedItem[]>([]);
  const [days, setDays] = useState(30);
  const fetchFn = useServerFn(getRecap);

  useEffect(() => setItems(readSavedItems()), []);

  const query = useQuery({
    queryKey: ["recap", days, items.map((i) => i.slug).sort().join(",")],
    queryFn: () => fetchFn({ data: { items, days } }),
    enabled: items.length > 0,
    staleTime: 60_000,
  });

  const data = query.data;
  const level = levelFor(data?.totals.points ?? 0);

  return (
    <div className="min-h-screen">
      <ScrollProgress />
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-4 py-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-tight">Rekap performa</h1>
            <p className="text-sm text-muted-foreground">
              Total poin, badge, pengeluaran, dan hasil auto-bid listing Anda per periode.
            </p>
          </div>
          <div className="flex gap-1 rounded-lg border border-border p-1">
            {PERIODS.map((p) => (
              <Button
                key={p.days}
                size="sm"
                variant={days === p.days ? "default" : "ghost"}
                className="h-7 px-2.5 text-xs"
                onClick={() => setDays(p.days)}
              >
                {p.label}
              </Button>
            ))}
          </div>
        </div>

        {items.length === 0 ? (
          <Card className="mt-5">
            <CardContent className="p-6 text-sm text-muted-foreground">
              Belum ada listing di perangkat ini.{" "}
              <Link to="/" hash="daftar" className="text-primary hover:underline">
                Daftarkan listing pertama Anda
              </Link>
              .
            </CardContent>
          </Card>
        ) : query.isPending ? (
          <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Menghitung rekap…
          </div>
        ) : !data ? null : (
          <>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Stat icon={Coins} label={`Belanja ${days} hari`} value={formatIDR(data.totals.spent)} />
              <Stat icon={MousePointerClick} label="Klik periode ini" value={<CountUp value={data.totals.clicks} />} />
              <Stat
                icon={Award}
                label={`Poin · level ${level.label}`}
                value={<CountUp value={data.totals.points} />}
              />
              <Stat icon={Repeat2} label="Auto-bid berjalan" value={<CountUp value={data.totals.autoBids} />} />
            </div>

            <Reveal>
              <Card className="mt-4">
                <CardHeader className="p-4 pb-1">
                  <CardTitle className="flex items-center gap-1.5 text-base">
                    <BarChart3 className="size-4 text-primary" /> Klik & belanja harian
                  </CardTitle>
                </CardHeader>
                <CardContent className="h-64 p-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.series} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                      <defs>
                        <linearGradient id="rc" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="var(--primary)" stopOpacity={0.35} />
                          <stop offset="100%" stopColor="var(--primary)" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="rs" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="var(--chart-2, var(--primary))" stopOpacity={0.28} />
                          <stop offset="100%" stopColor="var(--chart-2, var(--primary))" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                      <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={38} />
                      <Tooltip
                        contentStyle={{ fontSize: 12, borderRadius: 10, border: "1px solid var(--border)" }}
                        formatter={(v: number, n) => (n === "spent" ? formatIDR(v) : v)}
                      />
                      <Area type="monotone" dataKey="clicks" stroke="var(--primary)" fill="url(#rc)" strokeWidth={2} />
                      <Area
                        type="monotone"
                        dataKey="spent"
                        stroke="var(--chart-2, var(--primary))"
                        fill="url(#rs)"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Reveal>

            <Reveal>
              <Card className="mt-4">
                <CardHeader className="p-4 pb-2">
                  <CardTitle className="text-base">Per listing</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table className="text-xs">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="h-9">Listing</TableHead>
                          <TableHead className="h-9 text-right">Belanja</TableHead>
                          <TableHead className="h-9 text-right">Order</TableHead>
                          <TableHead className="h-9 text-right">Klik</TableHead>
                          <TableHead className="h-9 text-right">Poin</TableHead>
                          <TableHead className="h-9 text-right">Auto-bid</TableHead>
                          <TableHead className="h-9">Level</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.rows.map((r) => (
                          <TableRow key={r.slug}>
                            <TableCell className="py-2 font-medium">
                              <Link
                                to="/produk/$slug"
                                params={{ slug: r.slug }}
                                className="text-primary hover:underline"
                              >
                                {r.name}
                              </Link>
                            </TableCell>
                            <TableCell className="py-2 text-right tabular-nums">{formatIDR(r.spent)}</TableCell>
                            <TableCell className="py-2 text-right tabular-nums">{r.orders}</TableCell>
                            <TableCell className="py-2 text-right tabular-nums">{r.clicks}</TableCell>
                            <TableCell className="py-2 text-right tabular-nums">{r.points}</TableCell>
                            <TableCell className="py-2 text-right tabular-nums">{r.auto_bids}</TableCell>
                            <TableCell className="py-2">
                              <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
                                {levelFor(r.points).label}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </Reveal>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Coins;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
      <Card className="p-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Icon className="size-3.5 text-primary" /> {label}
        </div>
        <p className="mt-1 font-display text-xl font-semibold tabular-nums">{value}</p>
      </Card>
    </motion.div>
  );
}
